/**
 * Working out the key from what has been written.
 *
 * Four independent kinds of evidence, kept separate so the app can explain which
 * one convinced it:
 *
 *   1. Spelling      — writing B♭ is direct evidence for the keys that flatten B,
 *                      and evidence *against* the keys that do not.
 *   2. Diatonic fit  — how much of what you wrote belongs to the key at all.
 *   3. Chord fit     — a chord whose notes are all diatonic is stronger evidence
 *                      than the same notes scattered.
 *   4. Tonal centre  — correlation against the Krumhansl–Kessler key profiles,
 *                      which weight the tonic and dominant heavily. This is what
 *                      separates keys that share a signature-adjacent note set.
 *
 * Detection deliberately refuses to answer early. One note is consistent with
 * dozens of keys, so with too little evidence, or with two keys too close to call,
 * the honest answer is "no clear key" — which is also the right answer for genuinely
 * chromatic writing.
 */

import { MAJOR_SCALE_SEMITONES, naturalPitchClass, pitchClass, type Pitch } from './pitch';
import { KEY_SIGNATURES, type KeySignature } from './keys';
import type { ChordAnalysis } from './chords';

/**
 * Krumhansl–Kessler probe-tone profiles: how strongly each scale degree is felt to
 * belong, measured experimentally. Index 0 is the tonic.
 */
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

export interface KeyCandidate {
  signature: KeySignature;
  /** Combined evidence score. Comparable within one detection run only. */
  score: number;
  /** Share of the total evidence, 0-1. Across all candidates these sum to 1. */
  confidence: number;
  /** Whether the tonal centre reads as the major tonic or its relative minor. */
  mode: 'major' | 'minor';
  reasons: string[];
}

export interface KeyDetection {
  candidates: KeyCandidate[];
  /** The winner, or null when the evidence does not support any key. */
  best: KeyCandidate | null;
  /** False when there is too little to go on, or two keys are too close to call. */
  sufficient: boolean;
  /** Plain-language summary shown in the UI. */
  summary: string;
  noteCount: number;
}

/** Fewer notes than this and no key claim is honest. */
export const MIN_NOTES_FOR_DETECTION = 5;
/** Below this share of the probability mass, say "no clear key" rather than guessing. */
const MIN_CONFIDENCE = 0.45;

function pearson(a: number[], b: number[]): number {
  const n = a.length;
  const meanA = a.reduce((x, y) => x + y, 0) / n;
  const meanB = b.reduce((x, y) => x + y, 0) / n;
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < n; i++) {
    const x = a[i] - meanA;
    const y = b[i] - meanB;
    num += x * y;
    da += x * x;
    db += y * y;
  }
  const den = Math.sqrt(da * db);
  return den === 0 ? 0 : num / den;
}

function rotate(profile: number[], tonic: number): number[] {
  return profile.map((_, i) => profile[(i - tonic + 12 * 2) % 12]);
}

/** Pitch classes of the major scale on a given tonic. */
function diatonicSet(tonic: number): Set<number> {
  return new Set(MAJOR_SCALE_SEMITONES.slice(0, 7).map((s) => (tonic + s) % 12));
}

export function detectKey(notes: Pitch[], chords: ChordAnalysis[] = []): KeyDetection {
  const noteCount = notes.length;

  if (noteCount === 0) {
    return {
      candidates: [],
      best: null,
      sufficient: false,
      summary: 'Nothing written yet.',
      noteCount,
    };
  }

  // Pitch-class histogram for the tonal-centre correlation.
  const histogram = new Array(12).fill(0);
  for (const note of notes) histogram[pitchClass(note)] += 1;

  const candidates: KeyCandidate[] = KEY_SIGNATURES.map((signature) => {
    const reasons: string[] = [];
    const tonic = signature.semitone;
    const scale = diatonicSet(tonic);

    // --- 1. spelling evidence -------------------------------------------
    let spelling = 0;
    let matches = 0;
    let contradictions = 0;
    for (const note of notes) {
      const expected = signature.map[note.letter] ?? 0;
      if (note.alter === expected) {
        // Writing the accidental the signature calls for is real evidence;
        // writing an unaltered letter it does not touch is weak evidence.
        spelling += expected === 0 ? 0.25 : 1.5;
        if (expected !== 0) matches += 1;
      } else {
        spelling -= 1.5;
        contradictions += 1;
      }
    }
    if (matches > 0) {
      reasons.push(
        `${matches} note${matches === 1 ? '' : 's'} spelled exactly as ${signature.major} requires`,
      );
    }
    if (contradictions > 0) {
      reasons.push(
        `${contradictions} note${contradictions === 1 ? '' : 's'} contradict${contradictions === 1 ? 's' : ''} the signature`,
      );
    }

    // --- 2. diatonic fit --------------------------------------------------
    const inScale = notes.filter((n) => scale.has(pitchClass(n))).length;
    const diatonicFit = inScale / noteCount;
    if (diatonicFit === 1) reasons.push('every note belongs to the scale');

    // --- 3. chord fit -----------------------------------------------------
    let chordFit = 0;
    for (const chord of chords) {
      const allDiatonic = chord.pitches.every((p) => scale.has(pitchClass(p)));
      if (allDiatonic) chordFit += 1;
    }
    if (chordFit > 0) {
      reasons.push(`${chordFit} chord${chordFit === 1 ? '' : 's'} diatonic to ${signature.major}`);
    }

    // --- 4. tonal centre --------------------------------------------------
    const majorCorrelation = pearson(histogram, rotate(MAJOR_PROFILE, tonic));
    const relativeMinorTonic = (tonic + 9) % 12;
    const minorCorrelation = pearson(histogram, rotate(MINOR_PROFILE, relativeMinorTonic));
    const mode: 'major' | 'minor' = minorCorrelation > majorCorrelation ? 'minor' : 'major';
    const correlation = Math.max(majorCorrelation, minorCorrelation);
    if (correlation > 0.6) {
      reasons.push(
        `weighting points at ${mode === 'major' ? signature.major : signature.minor} as the centre`,
      );
    }

    const score =
      (spelling / noteCount) * 2.2 + diatonicFit * 2.6 + (chordFit / Math.max(1, chords.length)) * 1.4 + correlation * 2.0;

    return { signature, score, confidence: 0, mode, reasons };
  });

  candidates.sort((a, b) => b.score - a.score);

  // Turn the scores into a probability distribution, so confidence means "how much
  // better is this than every alternative" rather than an arbitrary scale. A key
  // that scores 1.4 clear of the field lands near 0.9; a chromatic passage, where
  // the top few keys sit within 0.3 of each other, lands near 0.2 — and that is
  // exactly when a confident answer would be a lie.
  const top = candidates[0];
  const runnerUp = candidates[1];
  const temperature = 0.5;
  const weights = candidates.map((c) => Math.exp((c.score - top.score) / temperature));
  const total = weights.reduce((a, b) => a + b, 0);
  candidates.forEach((candidate, i) => {
    candidate.confidence = weights[i] / total;
  });

  const enoughNotes = noteCount >= MIN_NOTES_FOR_DETECTION;
  const decisive = top.confidence >= MIN_CONFIDENCE;
  const sufficient = enoughNotes && decisive;

  const summary = !enoughNotes
    ? `Only ${noteCount} note${noteCount === 1 ? '' : 's'} so far — at least ${MIN_NOTES_FOR_DETECTION} are needed before a key can be claimed.`
    : !decisive
      ? `No clear key. ${top.signature.major} and ${runnerUp.signature.major} fit the writing about equally well, which is what genuinely chromatic writing looks like.`
      : `${top.mode === 'major' ? top.signature.major + ' major' : top.signature.minor} — ${top.reasons[0] ?? 'best overall fit'}.`;

  return {
    candidates: candidates.slice(0, 4),
    best: sufficient ? top : null,
    sufficient,
    summary,
    noteCount,
  };
}

/** Pitch class of a written note's letter, ignoring its accidental. */
export function letterClass(note: Pitch): number {
  return naturalPitchClass(note.letter);
}
